import {
  format as dateFnsFormat,
  isAfter,
  isBefore,
  isSameDay,
  isValid,
  parse,
  startOfDay,
} from 'date-fns';
import PropTypes from 'prop-types';
import React, { useMemo, useRef, useState } from 'react';
import InputWrapper from './input-wrapper';
import RangeDayPicker from './range-day-picker';

// used to separate the two dates of the range date
export const SPLITTER = ' - ';
export const DATE_FORMAT = 'EEE, d MMM yyyy';

const parseInputRange = (value, format) => {
  const [from, to] = value.split(SPLITTER);
  const fromDate = parse(from, format, new Date());
  const toDate = parse(to, format, new Date());
  return {
    from: fromDate,
    // if an earlier date was provided, then return an invalid date
    to: isBefore(toDate, fromDate) ? new Date(NaN) : toDate,
  };
};

const RangeDayPickerInput = React.forwardRef((props, ref) => {
  const {
    initialValue,
    value: valueProp,
    defaultMonth: defaultMonthProp,
    month: monthProp,
    open,
    disabled,
    onOpen,
    onClose,
    format = DATE_FORMAT,
    onChange,
    InputProps = {},
    DayPickerProps = {},
    canReverse = true,
    canPickSingleDay,
    ...otherProps
  } = props;
  const isControlled = useRef(valueProp !== undefined && valueProp !== null);
  const isMonthControlled = useRef(monthProp !== undefined && monthProp !== null);
  const defaultRangeDate = useMemo(() => parseInputRange(initialValue ?? '', format), [
    format,
    initialValue,
  ]);
  const [internalValue, setInternalValue] = useState(initialValue ?? '');
  const [internalRangeDate, setInternalRangeDate] = useState(defaultRangeDate);

  const rangeDate = isControlled.current ? parseInputRange(valueProp, format) : internalRangeDate;
  const value = isControlled.current ? valueProp : internalValue;

  const defaultMonth = useMemo(
    () => defaultMonthProp ?? (isValid(rangeDate.from) ? rangeDate.from : new Date()),
    [defaultMonthProp, rangeDate.from]
  );
  const [internalMonth, setInternalMonth] = useState(defaultMonth);
  const month = isMonthControlled.current ? monthProp : internalMonth;

  const handleInputChange = (e) => {
    const { value: inputValue } = e.target;
    const range = parseInputRange(inputValue, format);
    if (!isControlled.current) {
      setInternalValue(inputValue);
      setInternalRangeDate(range);
    }
    // if input two valid dates
    // and current displayed month is not between these two dates
    // then navigate to the start date's month
    if (
      isValid(range.from) &&
      isValid(range.to) &&
      (isBefore(month, range.from) || isAfter(month, range.to))
    ) {
      setInternalMonth(range.from);
    }
    if (onChange) onChange(range, inputValue);
  };

  const getRange = (currentClickedDate) => {
    const { from, to } = rangeDate;
    const startOfCurrentClickedDate = startOfDay(currentClickedDate);

    // `from` date is selected and `to` date is not selected
    if (!isValid(to)) {
      if (isSameDay(startOfCurrentClickedDate, from)) {
        return canPickSingleDay ? { from, to: startOfCurrentClickedDate } : { from, to: undefined };
      }
      if (isAfter(startOfCurrentClickedDate, from)) {
        return { from, to: currentClickedDate };
      }
      if (canReverse && isBefore(startOfCurrentClickedDate, from)) {
        return { from: currentClickedDate, to: from };
      }
    }

    return { from: currentClickedDate, to: undefined };
  };

  const handleDayClick = (date, modifiers) => {
    if (modifiers.disabled) return;
    const range = getRange(date);
    const fromDateString = isValid(range.from) ? dateFnsFormat(range.from, format) : '';
    const toDateString = isValid(range.to) ? dateFnsFormat(range.to, format) : '';
    const inputValue = `${fromDateString}${fromDateString ? SPLITTER : ''}${toDateString}`;
    if (!isControlled.current) {
      setInternalRangeDate(range);
      setInternalValue(inputValue);
    }
    if (onChange) onChange(range, inputValue);
  };

  const handleMonthChange = (date) => {
    setInternalMonth(date);
  };
  return (
    <InputWrapper
      ref={ref}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
      disabled={disabled}
      InputProps={{ value, onChange: handleInputChange, readOnly: true, ...InputProps }}
      datePicker={
        <RangeDayPicker
          modifiers={rangeDate}
          selectedDays={[rangeDate.from, rangeDate]}
          onDayClick={handleDayClick}
          month={month}
          onMonthChange={handleMonthChange}
          {...DayPickerProps}
        />
      }
      {...otherProps}
    />
  );
});

RangeDayPickerInput.propTypes = {
  initialValue: PropTypes.string,
  value: PropTypes.string,
  defaultMonth: PropTypes.instanceOf(Date),
  month: PropTypes.instanceOf(Date),
  open: PropTypes.bool,
  disabled: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  format: PropTypes.string,
  onChange: PropTypes.func,
  InputProps: PropTypes.object,
  DayPickerProps: PropTypes.object,
  canReverse: PropTypes.bool,
  canPickSingleDay: PropTypes.bool,
};
RangeDayPickerInput.defaultProps = {
  initialValue: undefined,
  value: undefined,
  defaultMonth: undefined,
  month: undefined,
  open: undefined,
  disabled: undefined,
  onOpen: undefined,
  onClose: undefined,
  format: undefined,
  onChange: undefined,
  InputProps: undefined,
  DayPickerProps: undefined,
  canReverse: undefined,
  canPickSingleDay: undefined,
};

export default RangeDayPickerInput;
